import { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Camera, Trash2, Plus, FileText, X, Paperclip, Pencil, Link2, Unlink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import FacturaManualForm from './FacturaManualForm';
import PlataForm, { tipLabel } from './PlataForm';
import SagaLinkPicker from './SagaLinkPicker';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const lei = (v) => `${(v ?? 0).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei`;
const fmtD = (iso) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '—');

const blobCache = new Map();
const fileUrl = (att) => att.url || `${API_URL}/furnizori/fisiere/${att.id}`;
const useAuthBlob = (att) => {
  const id = att?.id;
  const [url, setUrl] = useState(blobCache.get(id) || null);
  useEffect(() => {
    if (!id || blobCache.has(id)) { setUrl(blobCache.get(id) || null); return; }
    let alive = true;
    axios.get(fileUrl(att), { ...auth(), responseType: 'blob' })
      .then((r) => { const u = URL.createObjectURL(r.data); blobCache.set(id, u); if (alive) setUrl(u); })
      .catch(() => {});
    return () => { alive = false; };
  }, [id]); // eslint-disable-line
  return url;
};

export const AuthThumb = ({ att, onOpen, onDelete, size = 'h-20 w-20' }) => {
  const url = useAuthBlob(att);
  const isPdf = att.content_type === 'application/pdf';
  return (
    <div className={`relative group ${size} rounded-lg border bg-slate-50 overflow-hidden shrink-0`} data-testid={`atasament-${att.id}`}>
      <button type="button" className="w-full h-full flex items-center justify-center" onClick={() => onOpen(att)} title={att.original_filename}>
        {isPdf ? <FileText className="w-8 h-8 text-slate-400" /> : url ? <img src={url} alt="" className="w-full h-full object-cover" /> : <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
      </button>
      {onDelete && (
        <button type="button" onClick={() => onDelete(att)} className="absolute top-0.5 right-0.5 hidden group-hover:flex bg-white/90 rounded p-0.5 text-red-600" data-testid={`atasament-delete-${att.id}`}><X className="w-3.5 h-3.5" /></button>
      )}
    </div>
  );
};

export const Lightbox = ({ att, onClose }) => {
  const url = useAuthBlob(att);
  if (!att) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4" onClick={onClose} data-testid="lightbox">
      <button className="absolute top-4 right-4 text-white" onClick={onClose} data-testid="lightbox-close"><X className="w-7 h-7" /></button>
      {!url ? <Loader2 className="w-8 h-8 animate-spin text-white" />
        : att.content_type === 'application/pdf' ? <iframe title="pdf" src={url} className="w-full h-full bg-white rounded" onClick={(e) => e.stopPropagation()} />
        : <img src={url} alt="" className="max-h-full max-w-full object-contain rounded shadow-2xl" onClick={(e) => e.stopPropagation()} />}
    </div>
  );
};

export const UploadBtn = ({ onFile, busy, label, testId }) => {
  const ref = useRef(null);
  return (
    <>
      <input ref={ref} type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onFile(f); }} data-testid={`${testId}-input`} />
      <Button type="button" size="sm" variant="outline" onClick={() => ref.current?.click()} disabled={busy} data-testid={testId}>
        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Camera className="w-4 h-4 mr-1" />} {label}
      </Button>
    </>
  );
};

export const PlatiList = ({ plati, canEdit, busy, onUpload, onDeleteAtt, onEdit, onDelete, onView, currentId = null, compact = false }) => {
  const [openIds, setOpenIds] = useState({});
  return (
  <div className="space-y-2" data-testid="plati-list">
    {plati.map((p) => {
      const expanded = !compact || !!openIds[p.id];
      const nAtt = (p.atasamente || []).length;
      return (
      <div key={p.id} className="flex items-start gap-3 rounded-lg border bg-white p-2.5" data-testid={`plata-${p.id}`}>
        <div className="flex-1 min-w-0">
          <div className="text-sm flex flex-wrap items-center gap-x-2">
            <b className="text-green-700">{lei(p.suma)}</b>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 uppercase">{tipLabel(p.tip_plata)}</span>
            <span className="text-slate-500">· {fmtD(p.data)}</span>{p.observatii && <span className="text-slate-500">· {p.observatii}</span>}
            {compact && (
              <button type="button" onClick={() => setOpenIds((o) => ({ ...o, [p.id]: !o[p.id] }))} className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border ${nAtt ? 'text-slate-700 border-slate-300 hover:bg-slate-50' : 'text-slate-400 border-dashed'}`} title={nAtt ? `${nAtt} dovezi — click pentru a le vedea` : 'fără dovadă — click pentru a adăuga'} data-testid={`plata-dovezi-toggle-${p.id}`}>
                <Paperclip className="w-3 h-3" /> {nAtt || 'fără dovadă'}
              </button>
            )}
          </div>
          {(p.alocari || []).length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1" data-testid={`plata-alocari-${p.id}`}>
              {p.alocari.map((a) => (
                <span key={a.factura_id} className={`text-[11px] px-1.5 py-0.5 rounded border ${a.factura_id === currentId ? 'bg-blue-50 border-blue-200 text-blue-800 font-medium' : 'bg-slate-50 border-slate-200 text-slate-600'}`} title={a.total_factura != null ? `factură ${lei(a.total_factura)}` : ''}>
                  {a.nr_intrare || a.factura_id.slice(0, 8)}: {lei(a.suma)}
                </span>
              ))}
              {p.alocari.length > 1 && <span className="text-[11px] text-slate-400">· plată comună pe {p.alocari.length} facturi</span>}
            </div>
          )}
          <div className="text-[11px] text-slate-400 mt-0.5">înregistrată de {p.created_by} · {new Date(p.created_at).toLocaleString('ro-RO')}</div>
          {expanded && (
            <div className="flex flex-wrap gap-2 mt-2" data-testid={`plata-dovezi-${p.id}`}>
              {(p.atasamente || []).map((a) => <AuthThumb key={a.id} att={a} onOpen={onView} onDelete={canEdit ? onDeleteAtt : null} size="h-14 w-14" />)}
              {canEdit && <UploadBtn onFile={(f) => onUpload(p.id, f)} busy={busy} label={(p.atasamente || []).length ? 'Încă o dovadă' : 'Adaugă dovadă'} testId={`plata-upload-${p.id}`} />}
            </div>
          )}
        </div>
        {canEdit && (
          <div className="flex flex-col gap-1">
            <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => onEdit(p)} disabled={busy} title="Modifică alocarea" data-testid={`plata-edit-${p.id}`}><Pencil className="w-4 h-4" /></Button>
            <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => onDelete(p)} disabled={busy} data-testid={`plata-delete-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
          </div>
        )}
      </div>
      );
    })}
  </div>
  );
};

export default function FacturaDialog({ facturaId, onClose, onChanged, canEdit = true, suppliers = [] }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(null);
  const [edit, setEdit] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [editPay, setEditPay] = useState(null);
  const [linking, setLinking] = useState(false);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API_URL}/furnizori/facturi/${facturaId}/detalii`, auth()); setD(r.data); }
    catch { toast.error('Factura nu a putut fi încărcată'); onClose(); }
  }, [facturaId, onClose]);
  useEffect(() => { if (facturaId) { setD(null); setEdit(false); setLinking(false); load(); } }, [facturaId, load]);

  const refresh = () => { load(); onChanged?.(); };
  const run = async (fn, ok) => { setBusy(true); try { await fn(); if (ok) toast.success(ok); refresh(); } catch (e) { toast.error(e.response?.data?.detail || 'Eroare'); } finally { setBusy(false); } };

  const uploadInvoicePhoto = (f) => run(async () => { const fd = new FormData(); fd.append('file', f); await axios.post(`${API_URL}/furnizori/facturi/${facturaId}/atasamente`, fd, auth()); }, 'Poză adăugată');
  const uploadPaymentPhoto = (pid, f) => run(async () => { const fd = new FormData(); fd.append('file', f); await axios.post(`${API_URL}/furnizori/plati/${pid}/atasamente`, fd, auth()); }, 'Dovadă adăugată');
  const deleteAtt = (a) => window.confirm('Ștergi acest fișier?') && run(() => axios.delete(`${API_URL}/furnizori/atasamente/${a.id}`, auth()), 'Fișier șters');
  const deletePayment = (p) => window.confirm(`Ștergi plata de ${lei(p.suma)} din ${fmtD(p.data)}?`) && run(() => axios.delete(`${API_URL}/furnizori/plati/${p.id}`, auth()), 'Plată ștearsă');
  const deleteInvoice = async () => {
    if (!window.confirm(`Ștergi factura ${d.nr_intrare} (${d.denumire}) și plățile/pozele ei?`)) return;
    setBusy(true);
    try { await axios.delete(`${API_URL}/furnizori/facturi/${facturaId}`, auth()); toast.success('Factură ștearsă'); onChanged?.(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Eroare'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!facturaId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="factura-dialog">
        {!d ? <div className="flex items-center gap-2 text-slate-500 p-6"><Loader2 className="w-4 h-4 animate-spin" /> Se încarcă...</div> : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">{d.tip_doc && d.tip_doc !== 'Factură' ? d.tip_doc : 'Factura'} {d.nr_intrare} <span className="text-slate-400 font-normal">· {d.denumire}</span>
                {d.sursa === 'manual' && !d.saga_linked && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 uppercase" title="Introdus manual, neconfirmat în Saga">manual</span>}
                {d.sursa === 'manual' && d.saga_linked && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 uppercase" data-testid="factura-saga-linked-badge">manual + Saga ✓</span>}</DialogTitle>
              <DialogDescription>Data {fmtD(d.data)} · scadență {fmtD(d.scadent)}{d.inf_suplm ? ` · ${d.inf_suplm}` : ''}{d.saga_linked && d.manual_snapshot ? ` · introdus manual ca „${d.manual_snapshot.nr_intrare}” / ${lei(d.manual_snapshot.total)}` : ''}</DialogDescription>
            </DialogHeader>

            {edit && (
              <div className="rounded-lg border bg-slate-50 p-3" data-testid="factura-manual-edit">
                <FacturaManualForm initial={d} suppliers={suppliers} onCancel={() => setEdit(false)} onSaved={() => { setEdit(false); refresh(); }} />
              </div>
            )}
            {linking && <SagaLinkPicker facturaId={d.id} onCancel={() => setLinking(false)} onLinked={() => { setLinking(false); refresh(); }} />}
            {!edit && !linking && canEdit && d.sursa === 'manual' && (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => setEdit(true)} data-testid="factura-manual-edit-btn"><Pencil className="w-4 h-4 mr-1" /> Editează</Button>
                {!d.saga_linked && <Button size="sm" variant="outline" onClick={() => setLinking(true)} data-testid="factura-saga-link-btn"><Link2 className="w-4 h-4 mr-1" /> Leagă de documentul Saga…</Button>}
                {d.saga_linked && <Button size="sm" variant="outline" className="text-amber-700 border-amber-200" disabled={busy} onClick={() => window.confirm('Desfaci împerecherea? Documentul tău revine la datele introduse manual, iar rândul Saga reapare separat în listă.') && run(() => axios.post(`${API_URL}/furnizori/facturi/${facturaId}/dezleaga`, {}, auth()), 'Împerechere desfăcută')} data-testid="factura-saga-unlink-btn"><Unlink className="w-4 h-4 mr-1" /> Desfă împerecherea</Button>}
                {!d.saga_linked && <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={deleteInvoice} disabled={busy} data-testid="factura-manual-delete-btn"><Trash2 className="w-4 h-4 mr-1" /> Șterge documentul</Button>}
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm" data-testid="factura-sume">
              {[['Total document', lei(d.total), ''],
                ['Plată efectivă', d.diferenta != null ? <>{lei(d.platit)}{d.compensat > 0 && <span className="block text-[11px] font-normal text-violet-700">din care {lei(d.compensat)} compensat storno</span>}</> : '—', 'text-green-700'],
                ['Diferență', d.diferenta == null ? '—' : Math.abs(d.diferenta) < 0.005 ? '0,00 lei' : d.diferenta > 0 ? `+${lei(d.diferenta)}` : `−${lei(-d.diferenta)}`, d.diferenta == null ? 'text-slate-400' : Math.abs(d.diferenta) < 0.005 ? 'text-green-700' : d.diferenta > 0 ? 'text-blue-700' : 'text-red-600'],
                ['Neachitat Saga (informativ)', d.sursa === 'manual' && !d.saga_linked ? 'nu e în Saga' : d.neachitat > 0 ? lei(d.neachitat) : 'achitată în Saga', 'text-slate-600']].map(([l, v, c]) => (
                <div key={l} className="rounded-lg border bg-white px-3 py-2"><div className="text-[11px] uppercase text-slate-400">{l}</div><div className={`font-semibold ${c}`}>{v}</div></div>
              ))}
            </div>
            {d.total < 0 && (
              <div className="text-xs rounded-lg border border-violet-200 bg-violet-50 text-violet-800 px-3 py-2" data-testid="factura-storno-info">
                Factură storno — credit la furnizor de {lei(-d.total)} lei. {d.compensat_in?.length ? `Alocat pe: ${d.compensat_in.map((c) => `${c.nr} −${lei(c.suma)}`).join(', ')}.` : ''}{d.credit_ramas > 0.005 ? ` Credit nealocat: ${lei(d.credit_ramas)} lei — îl aloci din „Înregistrează plată” pe factura dorită, alegând sursa „Storno”.` : ''}
              </div>
            )}

            <section className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold flex items-center gap-1.5"><Paperclip className="w-4 h-4" /> Poze factură <span className="text-slate-400 font-normal">({d.poze_factura.length})</span></h4>
                {canEdit && <UploadBtn onFile={uploadInvoicePhoto} busy={busy} label="Adaugă poză factură" testId="factura-upload-photo" />}
              </div>
              {d.poze_factura.length === 0 ? <p className="text-xs text-slate-400">Nicio poză atașată.</p> : (
                <div className="flex flex-wrap gap-2" data-testid="factura-poze">{d.poze_factura.map((a) => <AuthThumb key={a.id} att={a} onOpen={setView} onDelete={canEdit ? deleteAtt : null} />)}</div>
              )}
            </section>

            <section className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold">Plăți care sting această factură <span className="text-slate-400 font-normal">({d.plati.length})</span></h4>
                {canEdit && !payOpen && !editPay && <Button size="sm" onClick={() => setPayOpen(true)} data-testid="plata-open-form"><Plus className="w-4 h-4 mr-1" /> Înregistrează plată</Button>}
              </div>
              {d.plati.length === 0 ? <p className="text-xs text-slate-400">Nicio plată alocată încă.</p> : (
                <PlatiList plati={d.plati} canEdit={canEdit} busy={busy} currentId={d.id} onUpload={uploadPaymentPhoto} onDeleteAtt={deleteAtt} onView={setView} onDelete={deletePayment} onEdit={(p) => { setEditPay(p); setPayOpen(false); }} />
              )}
            </section>

            {canEdit && payOpen && <PlataForm cod={d.cod} preselectId={d.id} onSaved={() => { setPayOpen(false); refresh(); }} onCancel={() => setPayOpen(false)} />}
            {canEdit && editPay && <PlataForm cod={d.cod} initial={editPay} onSaved={() => { setEditPay(null); refresh(); }} onCancel={() => setEditPay(null)} />}
            <p className="text-[11px] text-slate-400">Pozele sunt redimensionate automat (max 1600px, ~150–400 KB) și stocate în spațiul de fișiere al platformei, nu în baza de date.</p>
          </>
        )}
        <Lightbox att={view} onClose={() => setView(null)} />
      </DialogContent>
    </Dialog>
  );
}
